import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { 
  faSearch, faTag, faTimes, faSpinner 
} from '@fortawesome/free-solid-svg-icons';
import { TagsService, Tag } from '../../services/tags.service';
import { AlertService } from '../alert-service/alert-service';

export interface SearchFilters {
  searchTerm: string;
  selectedTag: string;
  selectedType?: string;
}

export interface SearchTypeOption {
  value: string;
  label: string;
}

export interface SearchConfig {
  showTextSearch: boolean;
  showTagFilter: boolean;
  textPlaceholder: string;
  tagPlaceholder: string;
  showTypeFilter?: boolean;
  typePlaceholder?: string;
  typeOptions?: SearchTypeOption[];
}

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [FormsModule, FontAwesomeModule, TranslateModule],
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit {
  @Input() config: SearchConfig = {
    showTextSearch: true,
    showTagFilter: true,
    textPlaceholder: 'SEARCH.PLACEHOLDER.DEFAULT',
    tagPlaceholder: 'SEARCH.PLACEHOLDER.TAGS'
  };

  @Input() initialFilters: SearchFilters = {
    searchTerm: '',
    selectedTag: ''
  };

  @Output() filtersChanged = new EventEmitter<SearchFilters>();
  @Output() searchCleared = new EventEmitter<void>();
  @Output() tagFilterCleared = new EventEmitter<void>();
  @Output() allFiltersCleared = new EventEmitter<void>();

  searchTerm: string = '';
  selectedTagFilter: string = '';
  selectedTypeFilter: string = '';

  tagFilterInput: string = '';
  showTagDropdown: boolean = false;
  filteredTagsForInput: Tag[] = [];
  availableTags: Tag[] = [];
  loadingTags = false;

  faSearch = faSearch;
  faTag = faTag;
  faTimes = faTimes;
  faSpinner = faSpinner;

  constructor(private tagsService: TagsService, private alertService: AlertService) {}

  ngOnInit(): void {
    this.searchTerm = this.initialFilters.searchTerm;
    this.selectedTagFilter = this.initialFilters.selectedTag;
    this.tagFilterInput = this.initialFilters.selectedTag;
    this.selectedTypeFilter = this.initialFilters.selectedType || '';

    if (this.config.showTagFilter) {
      this.loadAvailableTags();
    }
  }

  onSearchInput(): void {
    this.emitFilters();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchCleared.emit();
    this.emitFilters();
  }

  private loadAvailableTags(): void {
    this.loadingTags = true;
    this.tagsService.getTags().subscribe({
      next: (response) => {
        this.availableTags = response.tags || [];
        this.filteredTagsForInput = [...this.availableTags];
        this.loadingTags = false;
      },
      error: (err: any) => {
        this.alertService.showError(err);
      }
    });
  }

  onTagInputChange(): void {
    this.filterTagsForInput();
    this.applyTagFilterFromInput();
  }

  private filterTagsForInput(): void {
    if (!this.tagFilterInput.trim()) {
      this.filteredTagsForInput = [...this.availableTags];
      this.showTagDropdown = this.availableTags.length > 0;
      return;
    }

    this.filteredTagsForInput = this.availableTags.filter(tag =>
      tag.name.toLowerCase().includes(this.tagFilterInput.toLowerCase())
    );
    this.showTagDropdown = this.filteredTagsForInput.length > 0;
  }

  private applyTagFilterFromInput(): void {
    if (!this.tagFilterInput.trim()) {
      this.selectedTagFilter = '';
      this.emitFilters();
      return;
    }

    const exactMatch = this.availableTags.find(tag => 
      tag.name.toLowerCase() === this.tagFilterInput.toLowerCase()
    );

    if (exactMatch) {
      this.selectedTagFilter = exactMatch.name;
      this.emitFilters();
    } else {
      this.selectedTagFilter = '';
      this.emitFilters();
    }
  }

  selectTagFromDropdown(tag: Tag): void {
    this.tagFilterInput = tag.name;
    this.selectedTagFilter = tag.name;
    this.showTagDropdown = false;
    this.emitFilters();
  }

  onTagInputBlur(): void {
    setTimeout(() => {
      this.showTagDropdown = false;
    }, 200);
  }

  clearTagFilter(): void {
    this.selectedTagFilter = '';
    this.tagFilterInput = '';
    this.showTagDropdown = false;
    this.tagFilterCleared.emit();
    this.emitFilters();
  }

  onTypeFilterChange(): void {
    this.emitFilters();
  }

  clearTypeFilter(): void {
    this.selectedTypeFilter = '';
    this.emitFilters();
  }

  clearAllFilters(): void {
    this.searchTerm = '';
    this.selectedTagFilter = '';
    this.tagFilterInput = '';
    this.selectedTypeFilter = '';
    this.showTagDropdown = false;
    this.allFiltersCleared.emit();
    this.emitFilters();
  }

  private emitFilters(): void {
    const filters: SearchFilters = {
      searchTerm: this.searchTerm,
      selectedTag: this.selectedTagFilter,
      selectedType: this.selectedTypeFilter
    };
    this.filtersChanged.emit(filters);
  }

  get hasActiveFilters(): boolean {
    return !!(this.searchTerm.trim() || this.selectedTagFilter || this.selectedTypeFilter);
  }

  get hasMultipleFilters(): boolean {
    return [this.searchTerm.trim(), this.selectedTagFilter, this.selectedTypeFilter].filter(Boolean).length > 1;
  }
}